/**
 * Shared regex patterns and utility functions for Magic MD syntax scanning.
 *
 * Syntax-level regex/helpers re-exported from @magic-md/core/patterns (SSOT).
 * CodeMirror-specific utilities (statusBgClass, findDefineBlocks) stay local.
 */

export {
	BLOCK_RENDER_RE,
	COMPLETION_REF_TRIGGER_RE,
	COMPLETION_RENDER_TRIGGER_RE,
	CONTENT_HEADER_RE,
	DEFINE_CLOSE_RE,
	DEFINE_HEADER_RE as DEFINE_RE,
	FENCE_OPEN_RE as CONFIG_FENCE_RE,
	FENCE_OPEN_RE as CONFIG_OUTPUT_RE,
	FOR_CLOSE_RE,
	FOR_HEADER_RE,
	IMPORT_RE,
	INLINE_RENDER_RE,
	isClosingFence,
	isInsideCodeSpanInLine as isInsideCodeSpan,
	isMarkdownFenceClose,
	type MarkdownFenceOpenInfo,
	META_HEADER_RE,
	parseAttrs,
	parseDefineHeader,
	parseMarkdownFenceOpen,
	REF_FULL_RE,
	REF_RE,
	USE_HEADER_RE,
} from "@magic-md/core/patterns";

import {
	CONTENT_HEADER_RE,
	DEFINE_CLOSE_RE,
	DEFINE_HEADER_RE,
	FENCE_OPEN_RE,
	FOR_CLOSE_RE,
	FOR_HEADER_RE,
	IMPORT_RE,
	isClosingFence,
	META_HEADER_RE,
	parseDefineHeader,
	USE_HEADER_RE,
} from "@magic-md/core/patterns";
import { t as translate } from "@magic-md/i18n";
import type { ExecutionStatus } from "../../store/plugin-execution-store";
import type { DefineBlockInfo } from "./types";

function tr(key: string, params?: Record<string, unknown>): string {
	return translate("editor", key, params);
}

/** Info about a ::meta block range */
export interface MetaBlockInfo {
	domain: string;
	startLine: number;
	endLine: number;
	/** summary from config (e.g., exports list, key-value pairs) */
	summary: string;
}

/** Info about a ::use block range */
export interface UseBlockInfo {
	useId: string;
	source: string;
	isTemplate: boolean;
	modifiers: string;
	startLine: number;
	endLine: number;
}

/** Info about a ::content[id] block range */
export interface ContentBlockInfo {
	defId: string;
	isTemplate: boolean;
	summary: string;
	startLine: number;
	endLine: number;
}

/** Info about a ::import line */
export interface ImportLineInfo {
	namespace: string;
	source: string;
	lineNumber: number;
	startLine: number;
	endLine: number;
	outputRange?: { startLine: number; endLine: number };
	/** Picked symbol names (from pick="...") */
	pick: string[];
	/** Excluded symbol names (from exclude="...") */
	exclude: string[];
	/** Rename mapping (from rename="a:b, c:d") */
	rename: Record<string, string>;
}

/** Compute CSS class for status-aware define line background */
export function statusBgClass(status: ExecutionStatus): string {
	switch (status) {
		case "running":
			return "cm-magic-define-line cm-magic-define-running";
		case "success":
			return "cm-magic-define-line cm-magic-define-success";
		case "error":
			return "cm-magic-define-line cm-magic-define-error";
		default:
			return "cm-magic-define-line"; // idle & cached
	}
}

/* ── Pre-scan: find all define block ranges ── */
export function findDefineBlocks(doc: {
	lines: number;
	line(n: number): { text: string };
}): DefineBlockInfo[] {
	const blocks: DefineBlockInfo[] = [];
	let current: { defId: string; pluginName: string; startLine: number } | null =
		null;
	for (let i = 1; i <= doc.lines; i++) {
		const text = doc.line(i).text;
		const trimmedStart = text.trimStart();
		const parsed = parseDefineHeader(trimmedStart);
		if (parsed) {
			current = { defId: parsed.id, pluginName: parsed.pluginName, startLine: i };
			continue;
		}
		if (DEFINE_CLOSE_RE.test(text.trim()) && current) {
			blocks.push({
				...current,
				summary: tr("syntax.summary.runPlugin", { pluginName: current.pluginName }),
				endLine: i,
			});
			current = null;
		}
	}
	return blocks;
}

/* ── Pre-scan: find all meta block ranges ── */
export function findMetaBlocks(doc: {
	lines: number;
	line(n: number): { text: string };
}): MetaBlockInfo[] {
	const blocks: MetaBlockInfo[] = [];
	let current: {
		domain: string;
		startLine: number;
		configLines: string[];
	} | null = null;
	let inFence = false;

	for (let i = 1; i <= doc.lines; i++) {
		const text = doc.line(i).text;
		const trimmed = text.trim();

		if (!current) {
			const m = trimmed.match(META_HEADER_RE);
			if (m) {
				current = { domain: m[1], startLine: i, configLines: [] };
			}
		} else {
			if (trimmed.startsWith("```") && !inFence) {
				inFence = true;
				continue;
			}
			if (trimmed.startsWith("```") && inFence) {
				inFence = false;
				continue;
			}
			if (inFence) {
				current.configLines.push(text);
				continue;
			}
			if (/^::\s*$/.test(trimmed)) {
				// Build summary from config
				let summary = "";
				const configText = current.configLines.join("\n").trim();
				if (configText) {
					try {
						const parsed = JSON.parse(configText);
						if (parsed.exports) {
							summary = `exports=[${parsed.exports.join(", ")}]`;
						} else {
							const keys = Object.keys(parsed).slice(0, 3);
							summary = keys
								.map((k) => `${k}=${JSON.stringify(parsed[k])}`)
								.join("  ");
						}
					} catch {
						summary = tr("syntax.summary.jsonError");
					}
				}
				blocks.push({ ...current, endLine: i, summary });
				current = null;
			}
		}
	}
	return blocks;
}

/* ── Pre-scan: find all import line positions ── */
export function findImportLines(doc: {
	lines: number;
	line(n: number): { text: string };
}): ImportLineInfo[] {
	const result: ImportLineInfo[] = [];
	for (let i = 1; i <= doc.lines; i++) {
		const text = doc.line(i).text.trim();
		const m = text.match(IMPORT_RE);
		if (m) {
			const attrsStr = m[1];
			const pickMatch = attrsStr.match(/pick="([^"]*)"/);
			const fromMatch = attrsStr.match(/from="([^"]*)"/);
			const nsMatch = attrsStr.match(/ns="([^"]*)"/);
			const source = fromMatch?.[1] ?? "";
			const namespace = nsMatch?.[1] ?? "";
			const pick = pickMatch?.[1]
				? pickMatch[1]
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
				: [];
			const excludeMatch = attrsStr.match(/exclude="([^"]*)"/);
			const exclude = excludeMatch?.[1]
				? excludeMatch[1]
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
				: [];
			const renameMatch = attrsStr.match(/rename="([^"]*)"/);
			const rename: Record<string, string> = {};
			if (renameMatch?.[1]) {
				for (const pair of renameMatch[1].split(",")) {
					const [from, to] = pair.split(":").map((s) => s.trim());
					if (from && to) rename[from] = to;
				}
			}
			result.push({
				namespace,
				source,
				lineNumber: i,
				startLine: i,
				endLine: i,
				pick,
				exclude,
				rename,
			});
		}
	}
	return result;
}

/* ── Pre-scan: find all use block ranges ── */
export function findUseBlocks(doc: {
	lines: number;
	line(n: number): { text: string };
}): UseBlockInfo[] {
	const blocks: UseBlockInfo[] = [];
	let current: {
		useId: string;
		source: string;
		isTemplate: boolean;
		startLine: number;
		configLines: string[];
		activeFenceKind: "config" | "output" | "";
		activeFenceLength: number;
	} | null = null;

	for (let i = 1; i <= doc.lines; i++) {
		const text = doc.line(i).text;
		const trimmed = text.trim();

		if (!current) {
			const m = trimmed.match(USE_HEADER_RE);
			if (m) {
				const attrsStr = m[2];
				const fromMatch = attrsStr.match(/from="([^"]*)"/);
				current = {
					useId: m[1],
					source: fromMatch?.[1] ?? "",
					isTemplate: /template="true"/.test(m[2]),
					startLine: i,
					configLines: [],
					activeFenceKind: "",
					activeFenceLength: 0,
				};
			}
		} else {
			if (current.activeFenceKind) {
				if (isClosingFence(trimmed, current.activeFenceLength)) {
					current.activeFenceKind = "";
					current.activeFenceLength = 0;
				} else if (current.activeFenceKind === "config") {
					current.configLines.push(text);
				}
				continue;
			}

			const fenceMatch = FENCE_OPEN_RE.exec(trimmed);
			if (fenceMatch) {
				current.activeFenceKind = fenceMatch[2] as "config" | "output";
				current.activeFenceLength = fenceMatch[1].length;
				continue;
			}

			if (/^::\s*$/.test(trimmed)) {
				// Build modifiers summary
				let modifiers = "";
				const configText = current.configLines.join("\n").trim();
				if (configText) {
					try {
						const parsed = JSON.parse(configText);
						const parts: string[] = [];
						if (parsed.patch)
							parts.push(`patch={${Object.keys(parsed.patch).join(",")}}`);
						if (parsed.bind) {
							const binds = Object.entries(parsed.bind)
								.map(([k, v]) => `${k}→${v}`)
								.join(",");
							parts.push(`bind={${binds}}`);
						}
						if (parsed.with)
							parts.push(`with={${Object.keys(parsed.with).join(",")}}`);
						modifiers = parts.join("  ");
					} catch {
						modifiers = tr("syntax.summary.jsonError");
					}
				}
				blocks.push({
					useId: current.useId,
					source: current.source,
					isTemplate: current.isTemplate,
					modifiers,
					startLine: current.startLine,
					endLine: i,
				});
				current = null;
			}
		}
	}
	return blocks;
}

/**
 * Regex matching block openers that close with a standalone `::` line.
 * Used for nesting depth tracking inside content blocks.
 */
const BLOCK_OPENER_RE = /^::(define\[|content\[|meta\[|use\[|import\{)/;

/* ── Pre-scan: find all content define block ranges ── */
export function findContentBlocks(doc: {
	lines: number;
	line(n: number): { text: string };
}): ContentBlockInfo[] {
	const blocks: ContentBlockInfo[] = [];
	let current: {
		defId: string;
		isTemplate: boolean;
		startLine: number;
		nestingDepth: number;
		forNestingDepth: number;
	} | null = null;

	for (let i = 1; i <= doc.lines; i++) {
		const text = doc.line(i).text;
		const trimmed = text.trim();

		if (!current) {
			const m = trimmed.match(CONTENT_HEADER_RE);
			if (m) {
				const attrsStr = m[2];
				const isTemplate = /template/.test(attrsStr);
				current = {
					defId: m[1],
					isTemplate,
					startLine: i,
					nestingDepth: 0,
					forNestingDepth: 0,
				};
			}
		} else {
			// Track nested blocks (::define, ::meta, ::use) that also close with ::
			if (BLOCK_OPENER_RE.test(trimmed)) {
				current.nestingDepth++;
			} else if (FOR_HEADER_RE.test(trimmed)) {
				current.forNestingDepth++;
			} else if (FOR_CLOSE_RE.test(trimmed)) {
				if (current.forNestingDepth > 0) current.forNestingDepth--;
			} else if (/^::\s*$/.test(trimmed)) {
				if (current.nestingDepth > 0) {
					// This :: closes an inner nested block, not the content block
					current.nestingDepth--;
				} else if (current.forNestingDepth > 0) {
				} else {
					blocks.push({
						defId: current.defId,
						isTemplate: current.isTemplate,
						summary: current.isTemplate
							? tr("syntax.summary.template")
							: tr("syntax.summary.contentBlock"),
						startLine: current.startLine,
						endLine: i,
					});
					current = null;
				}
			}
		}
	}
	return blocks;
}

/* ── Pre-scan: find all line numbers inside ```output fences ── */

/**
 * Return the set of 1-based line numbers that fall inside an output fence
 * (including the opening and closing fence lines themselves).
 * Works across all block types: ::define, ::use, ::content, ::for.
 */
export function findOutputFenceLines(doc: {
	lines: number;
	line(n: number): { text: string };
}): Set<number> {
	const fenceLines = new Set<number>();
	let inOutputFence = false;
	let fenceLen = 3;
	// Track block nesting to only detect output fences at appropriate depth
	let blockDepth = 0;
	// Track whether we're inside a block-form ::import (header + output fence + ::)
	let inImportBlock = false;

	for (let i = 1; i <= doc.lines; i++) {
		const text = doc.line(i).text;
		const trimmed = text.trim();

		// Handle block-form ::import output fences (not nested in other blocks)
		if (blockDepth === 0 && !inImportBlock && IMPORT_RE.test(trimmed)) {
			// Check if next non-blank line starts an output fence
			let peek = i + 1;
			while (peek <= doc.lines && doc.line(peek).text.trim() === "") peek++;
			if (peek <= doc.lines) {
				const peekTrimmed = doc.line(peek).text.trim();
				if (peekTrimmed.match(/^`{3,}output/)) {
					inImportBlock = true;
				}
			}
		} else if (inImportBlock) {
			if (inOutputFence) {
				fenceLines.add(i);
				if (trimmed.length === fenceLen && trimmed === "`".repeat(fenceLen)) {
					inOutputFence = false;
				}
			} else if (trimmed.startsWith("```")) {
				const m = trimmed.match(/^(`{3,})output/);
				if (m) {
					inOutputFence = true;
					fenceLen = m[1].length;
					fenceLines.add(i);
				}
			} else if (DEFINE_CLOSE_RE.test(trimmed)) {
				// :: closes the block-form import
				inImportBlock = false;
			} else if (trimmed !== "") {
				// Non-empty, non-fence, non-close line — import block ended unexpectedly
				inImportBlock = false;
			}
			if (!inImportBlock && !inOutputFence) continue;
			if (inImportBlock) continue;
		}

		// Track block boundaries
		if (
			DEFINE_HEADER_RE.test(trimmed) ||
			USE_HEADER_RE.test(trimmed) ||
			CONTENT_HEADER_RE.test(trimmed) ||
			FOR_HEADER_RE.test(trimmed)
		) {
			blockDepth++;
		} else if (blockDepth > 0 && (DEFINE_CLOSE_RE.test(trimmed) || FOR_CLOSE_RE.test(trimmed))) {
			inOutputFence = false; // reset on block boundary
			blockDepth--;
		}

		if (blockDepth <= 0) continue;

		if (inOutputFence) {
			fenceLines.add(i);
			// Check for closing fence
			if (trimmed.length === fenceLen && trimmed === "`".repeat(fenceLen)) {
				inOutputFence = false;
			}
		} else if (trimmed.startsWith("```")) {
			const m = trimmed.match(/^(`{3,})output/);
			if (m) {
				inOutputFence = true;
				fenceLen = m[1].length;
				fenceLines.add(i); // opening fence line
			}
		}
	}
	return fenceLines;
}
